const cities = [
  { name: "Kenner", zips: "70062, 70065" },
  { name: "Metairie", zips: "70001, 70002, 70003, 70005, 70006" },
  { name: "Harahan", zips: "70123" },
  { name: "River Ridge", zips: "70123" },
  { name: "Westwego", zips: "70094" },
];

const benefits = [
  { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your home or office near MSY in minutes." },
  { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from any UPS or FedEx location — we handle every detail from there." },
  { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays." },
  { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
];

export default function KennerLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Kenner &amp; the Jefferson Parish Gateway
          </h2>
        </div>
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Kenner is unique among Louisiana cities: it is the only municipality in the state where you can be boarding an international flight within minutes of discovering your passport issue. Louis Armstrong New Orleans International Airport (MSY) is not adjacent to Kenner — it is in Kenner. The airport sits squarely within city limits, making Kenner the literal gateway for millions of travelers departing from the New Orleans metro area each year.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              That proximity creates a distinctive passport emergency pattern. Airport and airline employees — gate agents, cargo handlers, catering crews, and maintenance technicians — frequently discover that their own passports are expired when international transfer opportunities arise or when they are asked to deadhead on international routes. Ground crew at MSY who are suddenly needed at a hub in London, Frankfurt, or Bogotá face the same compressed timeline as any traveler, but often with less personal travel experience navigating the expedited passport process.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond airport employees, Kenner and the surrounding River Ridge, Harahan, and Westwego communities are home to a large population of working-class and middle-income families who book international vacations — particularly cruises and Caribbean all-inclusives — when deals appear. These are often last-minute decisions driven by promotional pricing, and the passport expiration check happens after booking rather than before. Standard processing through a Jefferson Parish post office can easily stretch three to five months during peak travel season, which is rarely compatible with a departure six to eight weeks out.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Petrochemical industry workers in the Kenner and Avondale area also face compressed passport timelines. Offshore rotation assignments to West Africa, the North Sea, or Southeast Asian platforms frequently arrive with 14 to 21 days notice. For workers whose passports have lapsed during onshore assignment periods, the urgency is real and the window is narrow. Fast Passport Center provides the professional, expedited processing that Kenner residents need — no long drive to the New Orleans Passport Agency required.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Kenner, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Jefferson Parish &amp; MSY Gateway</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Serving Kenner, River Ridge, Harahan, Metairie &amp; greater Jefferson Parish
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Remote service — start online, ship your documents overnight</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start your order online, then ship your documents overnight to us — no office visit required for most services. Most Kenner clients have passports back in hand within 2–9 business days.
                </p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap">
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Jefferson Parish — start online, ship overnight, and we handle the rest. No need to drive to the New Orleans Passport Agency on Poydras Street.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Metairie", href: "/cities/metairie" },
                    { name: "Slidell", href: "/cities/slidell" },
                    { name: "Hammond", href: "/cities/hammond" },
                    { name: "Houma", href: "/cities/houma" },
                    { name: "Baton Rouge", href: "/cities/baton-rouge" },
                  ].map((link) => (
                    <a key={link.href} href={link.href} className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap">
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Kenner Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a key={i} href="https://fastpassportcenter.com" className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}